import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Check,
  Shield,
  Zap,
  TrendingUp,
  Crown,
  Building,
  Loader2,
  CreditCard,
  CheckCircle2,
  XCircle,
  Gift,
  Building2 as Building2Icon
} from 'lucide-react';

const PaymentPage = () => {
  const { user, token, refreshUser } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan') || 'starter';
  const billingPeriod = searchParams.get('billing') || 'monthly';
  const t = (key) => getTranslation(language, key);
  
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'pending', 'error'
  const [duitkuLoaded, setDuitkuLoaded] = useState(false);

  // Load Duitku POP SDK script
  useEffect(() => {
    // Check if script already loaded
    if (window.checkout) {
      setDuitkuLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://app-prod.duitku.com/lib/js/duitku.js';
    script.async = true;
    script.onload = () => {
      console.log('Duitku SDK loaded');
      setDuitkuLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load Duitku SDK');
      toast.error('Gagal memuat payment gateway');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Yearly prices (pay 10 months for 12 months = ~17% discount)
  const yearlyPrices = {
    starter: { total: 1990000, monthly: 165833 },
    growth: { total: 4490000, monthly: 374166 },
    business: { total: 9990000, monthly: 832500 },
  };

  const formatPrice = (amount) => new Intl.NumberFormat('id-ID').format(amount);

  // Pricing synced with backend/config/plans.py
  const plans = {
    trial: {
      id: 'trial',
      name: 'Trial',
      price: 0,
      priceText: 'Rp 0',
      period: '30 hari',
      description: 'Coba semua fitur gratis',
      icon: <Zap className="w-5 h-5" />,
      aiAgents: 1,
      messages: 'Unlimited',
    },
    starter: {
      id: 'starter',
      name: 'Starter',
      price: billingPeriod === 'yearly' ? 1990000 : 199000,
      priceText: billingPeriod === 'yearly' ? `Rp ${formatPrice(yearlyPrices.starter.total)}` : 'Rp 199.000',
      monthlyEquivalent: billingPeriod === 'yearly' ? `Rp ${formatPrice(yearlyPrices.starter.monthly)}/bln` : null,
      period: billingPeriod === 'yearly' ? 'per tahun' : 'per bulan',
      description: 'Untuk UMKM yang baru memulai',
      icon: <Zap className="w-5 h-5" />,
      aiAgents: 1,
      messages: 5000,
    },
    growth: {
      id: 'growth',
      name: 'Growth',
      price: billingPeriod === 'yearly' ? 4490000 : 449000,
      priceText: billingPeriod === 'yearly' ? `Rp ${formatPrice(yearlyPrices.growth.total)}` : 'Rp 449.000',
      monthlyEquivalent: billingPeriod === 'yearly' ? `Rp ${formatPrice(yearlyPrices.growth.monthly)}/bln` : null,
      period: billingPeriod === 'yearly' ? 'per tahun' : 'per bulan',
      description: 'Untuk bisnis yang berkembang',
      icon: <TrendingUp className="w-5 h-5" />,
      aiAgents: 3,
      messages: 15000,
      popular: true,
    },
    business: {
      id: 'business',
      name: 'Business',
      price: billingPeriod === 'yearly' ? 9990000 : 999000,
      priceText: billingPeriod === 'yearly' ? `Rp ${formatPrice(yearlyPrices.business.total)}` : 'Rp 999.000',
      monthlyEquivalent: billingPeriod === 'yearly' ? `Rp ${formatPrice(yearlyPrices.business.monthly)}/bln` : null,
      period: billingPeriod === 'yearly' ? 'per tahun' : 'per bulan',
      description: 'Untuk bisnis dengan volume tinggi',
      icon: <Crown className="w-5 h-5" />,
      aiAgents: 10,
      messages: 50000,
    },
    enterprise: {
      id: 'enterprise',
      name: 'Enterprise',
      price: null,
      priceText: 'Custom',
      period: 'Hubungi kami',
      description: 'Solusi kustom untuk perusahaan',
      icon: <Building className="w-5 h-5" />,
      aiAgents: 'Unlimited',
      messages: 'Unlimited',
    }
  };

  const currentPlan = plans[selectedPlan] || plans.starter;

  // Guard: prevent downgrade from entering payment page
  const planOrder = { trial: 0, starter: 1, growth: 2, business: 3, enterprise: 4 };
  const userPlanRank = planOrder[user?.plan?.toLowerCase() || 'trial'] || 0;
  const selectedPlanRank = planOrder[selectedPlan] || 0;
  const isDowngrade = selectedPlanRank < userPlanRank && selectedPlanRank > 0;

  useEffect(() => {
    if (isDowngrade) {
      toast.error('Untuk downgrade, gunakan menu Langganan di Pengaturan.');
      navigate('/settings/subscription');
    }
  }, [isDowngrade, navigate]);

  const handlePayment = async () => {
    if (currentPlan.price === null) {
      // Enterprise - redirect to contact
      window.location.href = 'mailto:sales@nerava.id?subject=Enterprise Plan Inquiry';
      return;
    }

    if (currentPlan.price === 0) {
      // Trial plan - just activate
      try {
        await axios.post(
          `${API}/plan/upgrade`,
          { plan: 'trial' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Trial berhasil diaktifkan!');
        if (refreshUser) await refreshUser();
        navigate('/dashboard');
      } catch (error) {
        toast.error('Gagal mengaktifkan trial');
      }
      return;
    }

    if (!duitkuLoaded || !window.checkout) {
      toast.error('Payment gateway belum siap. Silakan refresh halaman.');
      return;
    }

    setProcessing(true);
    setPaymentStatus(null);

    try {
      // Create invoice via backend (POP API)
      const response = await axios.post(
        `${API}/payment/duitku/subscription/create`,
        {
          user_id: user.id,
          plan: currentPlan.id,
          payment_method: 'pop',
          user_email: user.email,
          user_name: user.name,
          billing_period: billingPeriod,
          phone_number: user.phone || user.phone_number || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.reference) {
        const reference = response.data.reference;
        
        // Open Duitku POP checkout using global checkout function
        window.checkout.process(reference, {
          successEvent: function(result) {
            console.log('Payment Success:', result);
            setPaymentStatus('success');
            toast.success('Pembayaran berhasil! Mengaktifkan paket...');
            setProcessing(false);
            
            // Refresh user data to get updated plan
            setTimeout(async () => {
              if (refreshUser) await refreshUser();
              navigate('/dashboard?payment=success');
            }, 2000);
          },
          pendingEvent: function(result) {
            console.log('Payment Pending:', result);
            setPaymentStatus('pending');
            toast.info('Pembayaran sedang diproses. Silakan selesaikan pembayaran Anda.');
            setProcessing(false);
          },
          errorEvent: function(result) {
            console.log('Payment Error:', result);
            setPaymentStatus('error');
            toast.error('Pembayaran gagal. Silakan coba lagi.');
            setProcessing(false);
          },
          closeEvent: function(result) {
            console.log('Popup closed');
            toast.info('Checkout dibatalkan');
            setProcessing(false);
          }
        });
      } else {
        throw new Error('Gagal membuat invoice pembayaran');
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal membuat pembayaran. Silakan coba lagi.';
      toast.error(errorMsg);
      setProcessing(false);
    }
  };

  // Payment status component
  const PaymentStatusBanner = () => {
    if (!paymentStatus) return null;

    const statusConfig = {
      success: {
        icon: <CheckCircle2 className="w-8 h-8 text-blue-500" />,
        title: 'Pembayaran Berhasil!',
        message: 'Paket Anda sedang diaktifkan...',
        bgColor: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200'
      },
      pending: {
        icon: <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />,
        title: 'Menunggu Pembayaran',
        message: 'Silakan selesaikan pembayaran Anda untuk mengaktifkan paket.',
        bgColor: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200'
      },
      error: {
        icon: <XCircle className="w-8 h-8 text-red-500" />,
        title: 'Pembayaran Gagal',
        message: 'Terjadi kesalahan. Silakan coba lagi.',
        bgColor: 'bg-red-50 dark:bg-red-900/20 border-red-200'
      }
    };

    const config = statusConfig[paymentStatus];

    return (
      <div className={`${config.bgColor} rounded-xl border p-6 mb-6 text-center`}>
        <div className="flex justify-center mb-3">{config.icon}</div>
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-1">{config.title}</h3>
        <p className="text-slate-600 dark:text-zinc-400">{config.message}</p>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] transition-colors">
        {/* Header */}
        <header className="bg-white dark:bg-zinc-900 border-b border-slate-200 dark:border-zinc-800 py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Nerava" className="w-10 h-10" />
              <span className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>Nerava</span>
            </Link>
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 dark:text-zinc-300">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Kembali
            </Button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <PaymentStatusBanner />
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Payment Info */}
            <div className="md:col-span-2 space-y-6">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2">{t('payment.title')}</h1>
                <p className="text-slate-600 dark:text-zinc-400">
                  {t('payment.subtitle')} <span className="font-semibold text-indigo-600 dark:text-indigo-400">{currentPlan.name}</span>
                </p>
              </div>

              {/* Payment Info Card */}
              {currentPlan.price > 0 && (
                <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-950/40 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">Pembayaran Aman</h2>
                      <p className="text-sm text-slate-500 dark:text-zinc-400">Powered by Duitku</p>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-[#0A0A0A]/50 rounded-lg p-4 mb-4">
                    <p className="text-sm text-slate-600 dark:text-zinc-400 mb-3">
                      Metode pembayaran yang tersedia:
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-zinc-300">
                        <Check className="w-4 h-4 text-blue-500" />
                        QRIS (Semua E-wallet)
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 dark:text-zinc-300">
                        <Check className="w-4 h-4 text-blue-500" />
                        Virtual Account
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 dark:text-zinc-300">
                        <Check className="w-4 h-4 text-blue-500" />
                        OVO, DANA, ShopeePay
                      </div>
                      <div className="flex items-center gap-2 text-slate-700 dark:text-zinc-300">
                        <Check className="w-4 h-4 text-blue-500" />
                        BCA, BNI, Mandiri, BRI
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    Klik tombol "Bayar Sekarang" untuk memilih metode pembayaran dan menyelesaikan transaksi.
                  </p>
                </div>
              )}

              {/* Trial activation message */}
              {currentPlan.price === 0 && (
                <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-blue-700 p-6">
                  <h2 className="text-lg font-semibold text-blue-800 dark:text-indigo-200 mb-2">
                    <Gift className="w-5 h-5 inline mr-2" />Aktivasi Trial Gratis
                  </h2>
                  <p className="text-indigo-700 dark:text-indigo-300">
                    Klik tombol di bawah untuk mengaktifkan trial 30 hari dengan akses penuh ke semua fitur Nerava.
                  </p>
                </div>
              )}

              {/* Enterprise contact message */}
              {currentPlan.price === null && (
                <div className="bg-slate-100 dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-2">
                    <Building2Icon className="w-5 h-5 inline mr-2" />Enterprise Plan
                  </h2>
                  <p className="text-slate-600 dark:text-zinc-400 mb-4">
                    Untuk paket Enterprise dengan fitur custom, silakan hubungi tim sales kami.
                  </p>
                  <div className="space-y-2 text-sm text-slate-600 dark:text-zinc-400">
                    <p><CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />Dedicated Support</p>
                    <p><CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />Custom Integration</p>
                    <p><CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />SLA Guarantee 99.9%</p>
                    <p><CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />Account Manager</p>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="md:col-span-1">
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6 sticky top-4">
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4">{t('payment.orderSummary')}</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-zinc-400">{t('payment.package')}</span>
                    <span className="font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                      {currentPlan.icon}
                      {currentPlan.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-zinc-400">AI Agent</span>
                    <span className="text-slate-900 dark:text-zinc-50">{currentPlan.aiAgents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-zinc-400">Pesan/bulan</span>
                    <span className="text-slate-900 dark:text-zinc-50">
                      {typeof currentPlan.messages === 'number' 
                        ? new Intl.NumberFormat('id-ID').format(currentPlan.messages)
                        : currentPlan.messages
                      }
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-zinc-400">{t('payment.period')}</span>
                    <span className="text-slate-900 dark:text-zinc-50 capitalize" data-testid="billing-period-display">
                      {billingPeriod === 'yearly' ? '1 Tahun' : currentPlan.period}
                    </span>
                  </div>
                </div>

                <div className="border-t border-slate-200 dark:border-zinc-800 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-slate-900 dark:text-zinc-50">{t('payment.total')}</span>
                    <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400" data-testid="payment-total">
                      {currentPlan.priceText}
                    </span>
                  </div>
                  {currentPlan.monthlyEquivalent && (
                    <p className="text-right text-xs text-green-600 dark:text-green-400 mt-1" data-testid="monthly-equivalent">
                      ~{currentPlan.monthlyEquivalent}
                    </p>
                  )}
                </div>

                <Button 
                  onClick={handlePayment}
                  disabled={processing || paymentStatus === 'success'}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6"
                  data-testid="payment-submit-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Memproses...
                    </>
                  ) : paymentStatus === 'success' ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Pembayaran Berhasil
                    </>
                  ) : currentPlan.price === null ? (
                    'Hubungi Sales'
                  ) : currentPlan.price === 0 ? (
                    'Aktifkan Trial'
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Bayar Sekarang
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500 dark:text-zinc-400">
                  <Shield className="w-4 h-4" />
                  {t('payment.securePayment')}
                </div>

                {/* All features included */}
                <div className="mt-6 pt-4 border-t border-slate-200 dark:border-zinc-800">
                  <p className="text-xs font-semibold text-slate-700 dark:text-zinc-300 mb-2">Sudah termasuk:</p>
                  <div className="space-y-1 text-xs text-slate-500 dark:text-zinc-400">
                    <p className="flex items-center gap-1"><Check className="w-3 h-3 text-blue-500" /> WhatsApp, IG, FB</p>
                    <p className="flex items-center gap-1"><Check className="w-3 h-3 text-blue-500" /> CRM & Pipeline</p>
                    <p className="flex items-center gap-1"><Check className="w-3 h-3 text-blue-500" /> Broadcast</p>
                    <p className="flex items-center gap-1"><Check className="w-3 h-3 text-blue-500" /> Payment Integration</p>
                    <p className="flex items-center gap-1"><Check className="w-3 h-3 text-blue-500" /> Analytics</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentPage;
